import React, { useState } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useForm, FormProvider } from 'react-hook-form';
import Input from 'components/Common/Input';
import Select from 'components/Common/Select';
import TelInput from 'components/Common/TelInput';
import AutoComplete from 'components/Common/Autocomplete';
import { useGetCountries, useGetStates } from 'apis/country/hook';
import { Timezones } from 'utils/timezones';
import { useNavigate, useParams } from 'react-router-dom';
import { useCreateOrUpdateLocation, useGetLocation, useGetLocations } from '@/apis/business/setting/location/hook';
import { useGetBusinessProfile } from '@/apis/business/hook';
import LocationFormSkeleton from './LocationFormSkeleton';
import Button from '@/components/Common/Button';
import Radio from '@/components/Common/Radio';
import ConfirmModal from '@/components/Common/ConfirmModal';
import { useGetTeamMembers } from '@/apis/business/setting/team/hook';
import GoBackButtonIcon from '@/assets/icons/GoBackButtonIcon';
import LocationIcon from '@/assets/icons/LocationIcon';
import BusinessSchedule from './businessHours';
// import { zodResolver } from '@hookform/resolvers/zod';
// import { locationFormSchema } from '@/utils/schema/validator/LocationFormValidations';

export default function LocationForm() {
  const navigate = useNavigate();
  const { locationId } = useParams<{ locationId: string }>();
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [pendingPayload, setPendingPayload] = useState<any>(null);

  const { data: LocationData, isLoading } = useGetLocation({ include: 'address' });
  const { data: BusinessProfile } = useGetBusinessProfile({ include: 'business_plan' });
  const { data: LocationsData } = useGetLocations({ page: 1, per_page: 1 });
  const { data: teamMembers, isLoading: isTeamMembersLoading } = useGetTeamMembers({
    page: 1,
    filter: { role_eq: '2' }
  });
  const methods = useForm({
    // resolver: zodResolver(locationFormSchema),
    defaultValues: {
      location: { business_hours: { ...defaultBusinessSchedule }, physical_location_exists: true }
    },
    values: {
      location: {
        ...LocationData,
        physical_location_exists: LocationData?.physical_location_exists ?? true,
        business_hours: LocationData?.business_hours ?? defaultBusinessSchedule,
        address_attributes: LocationData?.address
      }
    }
  });
  const { handleSubmit, control, watch, getValues, setValue } = methods;

  const locationCountry = watch('location.address_attributes.country');
  const physicalLocation = watch('location.physical_location_exists');
  const isPhysicalLocation = String(physicalLocation) === 'true';

  const { data: countries, isLoading: isCountriesLoading } = useGetCountries();
  const { data: states, isLoading: isStatesLoading } = useGetStates(locationCountry);
  const { mutate: handleLocation, isPending } = useCreateOrUpdateLocation();

  const planName = BusinessProfile?.business_plan?.name || 'plan';
  const includedLocations = BusinessProfile?.business_plan?.included_locations ?? 0;
  const additionalLocationCharge = BusinessProfile?.business_plan?.additional_per_location_charge ?? 0;
  const locationCount = LocationsData?.meta?.total_count ?? 0;
  const isLocationLimitReached =
    !locationId && Boolean(BusinessProfile?.business_plan) && locationCount >= includedLocations;

  const submitLocation = (data: any) => {
    const payload = {
      ...data,
      physical_location_exists: data.physical_location_exists === 'true' ? true : false
    };
    handleLocation(payload);
  };

  const onSubmit = async (data: any) => {
    if (isLocationLimitReached) {
      setPendingPayload(data);
      setShowLimitModal(true);
      return;
    }
    submitLocation(data);
  };

  const handleConfirmAddLocation = () => {
    if (!pendingPayload) return;
    setShowLimitModal(false);
    submitLocation(pendingPayload);
    setPendingPayload(null);
  };

  const handleCloseLimitModal = () => {
    setShowLimitModal(false);
    setPendingPayload(null);
  };

  if (isLoading) return <LocationFormSkeleton />;
  return (
    <FormProvider {...methods}>
      <Box component='form' onSubmit={handleSubmit(onSubmit)}>
        <Box display='flex' alignItems='start' mb={3} gap={0.8}>
          <Box
            onClick={() => navigate(-1)}
            display='flex'
            alignItems='center'
            justifyContent='center'
            sx={{ cursor: 'pointer' }}
          >
            <GoBackButtonIcon />
          </Box>
          <Typography variant='h6' fontWeight={500}>
            {locationId ? 'Edit Location' : 'Add New Location'}
          </Typography>
        </Box>
        <Paper
          elevation={0}
          sx={{
            backgroundColor: '#fff',
            borderRadius: '12px',
            p: 3,
            border: '1px solid #ECECEC'
          }}
        >
          {/* Header */}
          <Box>
            <Typography fontWeight={500} mb={1}>
              Location
            </Typography>
            <Radio
              name='location.physical_location_exists'
              control={control}
              options={[
                { label: 'Physical', value: true },
                { label: 'Remote', value: false }
              ]}
            />

            <Grid container spacing={3} mt={1}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Input name='location.name' label='Location Friendly Name' control={control} fullWidth required />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Input name='location.contact_name' label='Contact Person' control={control} fullWidth />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Input name='location.contact_email' label='Email' control={control} fullWidth required />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TelInput
                  name='location.contact_phone'
                  label='Business Number'
                  control={control}
                  defaultCountry='US'
                  fullWidth
                />
              </Grid>
            </Grid>

            {/* Address Information */}
            <Typography variant='subtitle1' fontWeight={500} mt={4} mb={1}>
              Address Information
            </Typography>

            <Grid container spacing={3}>
              {isPhysicalLocation && (
                <>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Input
                      name='location.address_attributes.street_address'
                      label='Street Address'
                      control={control}
                      fullWidth
                      required={isPhysicalLocation}
                      rules={isPhysicalLocation ? { required: 'Street address is required' } : undefined}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Input
                      name='location.address_attributes.address_line_2'
                      label='Address Line 2'
                      control={control}
                      fullWidth
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <Input
                      name='location.address_attributes.city'
                      label='City'
                      control={control}
                      required={isPhysicalLocation}
                      rules={isPhysicalLocation ? { required: 'City is required' } : undefined}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Input
                      name='location.address_attributes.postal_code'
                      label='Postal/Zip Code'
                      control={control}
                      fullWidth
                      required={isPhysicalLocation}
                      rules={isPhysicalLocation ? { required: 'Postal or zip code is required' } : undefined}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <Select
                      name='location.address_attributes.country'
                      label='Country'
                      control={control}
                      fullWidth
                      required={isPhysicalLocation}
                      rules={isPhysicalLocation ? { required: 'Country is required' } : undefined}
                      isLoading={isCountriesLoading}
                      options={countries || []}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Select
                      name='location.address_attributes.state'
                      label='State / Province / Region'
                      control={control}
                      fullWidth
                      required={isPhysicalLocation}
                      rules={isPhysicalLocation ? { required: 'State / province / region is required' } : undefined}
                      isLoading={isStatesLoading}
                      options={states || []}
                    />
                  </Grid>
                </>
              )}

              <Grid size={{ xs: 12, md: 6 }}>
                <AutoComplete
                  name='location.address_attributes.time_zone'
                  label='Time Zone'
                  control={control}
                  options={Timezones}
                  required
                />
              </Grid>
            </Grid>

            {/* Grant Access */}
            <Typography variant='subtitle1' fontWeight={500} mt={4} mb={1}>
              Grant Access
            </Typography>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Select
                  name='location.user_ids'
                  label='Select Location Admins'
                  disabled={isTeamMembersLoading}
                  multiple
                  control={control}
                  options={
                    teamMembers?.data
                      ? teamMembers.data.map((member) => ({ label: member.name, value: member.id }))
                      : []
                  }
                />
              </Grid>
            </Grid>
            <Grid container>
              <Grid size={12}>
                <BusinessSchedule control={control} getValues={getValues} setValue={setValue} />
              </Grid>
            </Grid>
          </Box>
        </Paper>
        <Box display='flex' flexDirection='row' gap={1} justifyContent='end' mt={3}>
          <Button
            color='secondary'
            variant='outlined'
            sx={{ border: '1px solid #F3F3F3', background: '#fff' }}
            onClick={() => navigate(-1)}
          >
            Cancel
          </Button>
          <Button type='submit' variant='contained' isPending={isPending} color='primary' sx={{ display: 'block' }}>
            {locationId ? 'Save Location' : 'Add Location'}
          </Button>
        </Box>
      </Box>

      <ConfirmModal
        open={showLimitModal}
        onClose={handleCloseLimitModal}
        onConfirm={handleConfirmAddLocation}
        title='Add another location?'
        description={`Your ${planName} includes ${includedLocations} business location${
          includedLocations === 1 ? '' : 's'
        }, which ${includedLocations === 1 ? 'is' : 'are'} already in use. Adding another location will cost an additional $${additionalLocationCharge}/month.`}
        confirmText={`Confirm & Add Location - $${additionalLocationCharge}/mo`}
        icon={
          <Box color='primary.main' display='flex'>
            <LocationIcon size={32} />
          </Box>
        }
      />
    </FormProvider>
  );
}

const defaultBusinessSchedule = {
  monday: { open: false, from_time: '', to_time: '', breaks: [] },
  tuesday: { open: false, from_time: '', to_time: '', breaks: [] },
  wednesday: { open: false, from_time: '', to_time: '', breaks: [] },
  thursday: { open: false, from_time: '', to_time: '', breaks: [] },
  friday: { open: false, from_time: '', to_time: '', breaks: [] },
  saturday: { open: false, from_time: '', to_time: '', breaks: [] },
  sunday: { open: false, from_time: '', to_time: '', breaks: [] }
};
